import React, { useEffect, useState } from "react";

import { Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/modal/Modal";
import WelcomeCard from "../../components/WelcomeCard";
import {
  aiCareerTitles,
  aiSkills as aiSkillsOptions,
  countries,
  race as raceOptions,
} from "../../constants/constants";
import Eye from "../../images/svgs/Eye";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { api1 } from "../../utils/axiosInstance";
import Sidebar from "../../components/Sidebar";
import { toast } from "react-toastify";
import SecondaryLoader from "../../components/SecondaryLoader/SecondaryLoader";

const statusColors = {
  false: "bg-pending text-white",
  true: "bg-accept text-white",
};

const MentorsList = () => {
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [gender, setGender] = useState("");
  const [race, setRace] = useState("");
  const [mentoringExperience, setMentoringExperience] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [openForGroup, setOpenForGroup] = useState("");
  const [openToOtherServices, setOpenToOtherServices] = useState("");
  const [aiSkills, setAiSkills] = useState("");
  const [aiCareers, setAiCareers] = useState("");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState();
  const [emailMessage, setEmailMessage] = useState();
  const [mentorId, setMentorId] = useState(null);
  const navigate = useNavigate();
  const formattedDate = (date) => {
    const fdate = new Date(date);
    const formattedDate = fdate.toLocaleString();
    return formattedDate;
  };

  const fetchAllMentorReviews = async () => {
    try {
      setLoading(true);
      const res = await api1.get(`/api/mentorProfile/get/mentors`);
      const mentors = res.data.data;

      // Reviews are now included in the mentors response from the backend
      setData(mentors);
    } catch (error) {
      console.error("Failed to fetch mentors", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllMentorReviews();
  }, []);

  const handleModalOpen = (mentorId) => {
    setMentorId(mentorId);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setEmailMessage(null);
    setEmailSubject(null);
    setShowModal(false);
  };

  const handleSendEmail = async () => {
    if (emailMessage && emailSubject) {
      try {
        await api1.post(API_ENDPOINTS.SEND_EMAIL_TO_MENTOR, {
          mentorId,
          subject: emailSubject,
          message: emailMessage,
        });
      } catch (error) {
        console.error(error);
      } finally {
        handleModalClose();
      }
    } else {
      toast.error("Please fill the email");
    }
  };

  return (
    <Sidebar>
      <Modal
        isOpen={showModal}
        onClose={handleModalClose}
        width="w-[400px] md:w-[600px]"
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Send Email to Mentor
          </h2>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Subject
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#7E5BEF]"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Message
              </label>
              <textarea
                rows={6}
                className="w-full border border-gray-300 rounded px-3 py-2 mt-1 resize-none focus:outline-none focus:ring-2 focus:ring-[#7E5BEF]"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Write your message here..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={handleModalClose}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                className="px-4 py-2 rounded bg-[#7E5BEF] text-white hover:bg-[#674dd0]"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <div className="flex flex-col gap-6">
        <WelcomeCard title={"Mentors"} />
        <div className="p-4 bg-white rounded shadow mb-6">
          <h2 className="text-lg font-semibold mb-4">Filter Mentors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              className="border px-3 py-2 rounded w-full"
            />
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="border px-3 py-2 rounded w-full"
            >
              <option value="">Select country</option>
              {countries.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
              className="border px-3 py-2 rounded w-full"
            />
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="border px-3 py-2 rounded w-full"
            >
              <option value="">Gender</option>
              <option value={1}>Male</option>
              <option value={2}>Female</option>
              {/* <option value="Other">Other</option> */}
            </select>
            <select
              value={race}
              onChange={(e) => setRace(e.target.value)}
              className="border px-3 py-2 rounded w-full"
            >
              <option value="">Select Race</option>
              {raceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={mentoringExperience}
              onChange={(e) => setMentoringExperience(e.target.value)}
              className="border px-3 py-2 rounded w-full"
            >
              <option value="">Mentoring Experience</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            <input
              type="number"
              value={yearsExperience}
              onChange={(e) => setYearsExperience(e.target.value)}
              placeholder="Years of Mentoring Experience"
              className="border px-3 py-2 rounded w-full"
            />
            <select
              value={openForGroup}
              onChange={(e) => setOpenForGroup(e.target.value)}
              className="border px-3 py-2 rounded w-full"
            >
              <option value="">Open for Group Mentoring</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            <select
              value={openToOtherServices}
              onChange={(e) => setOpenToOtherServices(e.target.value)}
              className="border px-3 py-2 rounded w-full"
            >
              <option value="">Open to Other Services</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            <select
              value={aiSkills}
              onChange={(e) => setAiSkills(e.target.value)}
              className="border px-3 py-2 rounded w-full"
            >
              <option value="">AI Skills of Interest</option>
              {aiSkillsOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* AI Interested Careers */}

            <select
              value={aiCareers}
              onChange={(e) => setAiCareers(e.target.value)}
              className="border px-3 py-2 rounded w-full"
            >
              <option value="">AI Interested Careers</option>
              {aiCareerTitles.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-6 bg-white min-h-screen">
          {/* Appointments Table */}
          <div className="rounded-lg w-full">
            <h3 className="text-lg font-semibold mb-4">Mentor Lists</h3>
            {loading ? (
              <SecondaryLoader message="Fetching mentors and their reviews..." />
            ) : (
              <div className="overflow-x-auto w-full">
                <table className="w-full border-collapse">
                <thead className="border-t border-b">
                  <tr className="uppercase text-sm">
                    <th className="py-3 px-6 text-left w-1/4">Basic Info</th>
                    <th className="py-3 px-6 text-center w-1/4">
                      Created Date
                    </th>
                    <th className="py-3 px-6 text-center w-1/4">
                      Verification Status
                    </th>
                    <th className="py-3 px-6 text-center w-1/4">Reviews</th>
                    <th className="py-3 px-6 text-center w-1/4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data
                    ?.filter((mentor) => {
                      const fullName =
                        `${mentor.firstName} ${mentor.lastName}`.toLowerCase();
                      const mentorCountry = mentor.country?.toLowerCase() || "";
                      const mentorCity = mentor.city?.toLowerCase() || "";
                      const mentorRace = mentor.race?.toLowerCase() || "";
                      const mentorGender = mentor.gender;
                      const mentorMentoringExp =
                        mentor.formalMentoringOrCoachingExperience;
                      const mentorYearsExp = mentor.yearsOfMentoring;
                      const mentorOpenGroup = mentor.openToGroup;
                      const mentorOpenServices =
                        mentor.openToConsulting ||
                        mentor.openToFacilitating ||
                        mentor.openToVolunteer;

                      return (
                        (!search ||
                          fullName.includes(search.toLowerCase()) ||
                          mentor.email
                            .toLowerCase()
                            .includes(search.toLowerCase())) &&
                        (!country ||
                          mentorCountry.includes(country.toLowerCase())) &&
                        (!city || mentorCity.includes(city.toLowerCase())) &&
                        (!gender || mentorGender == gender) && // Note: == for loose comparison
                        (!race || mentorRace.includes(race.toLowerCase())) &&
                        (!mentoringExperience ||
                          (mentoringExperience === "Yes"
                            ? mentorMentoringExp === "yes" ||
                              mentorMentoringExp === 1
                            : mentorMentoringExp === "no" ||
                              mentorMentoringExp === 0)) &&
                        (!yearsExperience ||
                          (mentorYearsExp === "1_2_years" &&
                            parseInt(yearsExperience) <= 2) ||
                          (mentorYearsExp === "3_5_years" &&
                            parseInt(yearsExperience) <= 5) ||
                          (mentorYearsExp === "5+_years" &&
                            parseInt(yearsExperience) > 5)) &&
                        (!openForGroup ||
                          (openForGroup === "Yes"
                            ? mentorOpenGroup === "yes"
                            : mentorOpenGroup === "no")) &&
                        (!aiSkills ||
                          (mentor.technicalAISkills &&
                            mentor.technicalAISkills.some((skill) =>
                              skill
                                .toLowerCase()
                                .includes(aiSkills.toLowerCase())
                            ))) &&
                        (!aiCareers ||
                          (mentor.aiCareers &&
                            mentor.aiCareers.some((career) =>
                              career
                                .toLowerCase()
                                .includes(aiCareers.toLowerCase())
                            ))) &&
                        (!openToOtherServices ||
                          (openToOtherServices === "Yes"
                            ? mentorOpenServices === "yes"
                            : mentorOpenServices === "no"))
                      );
                    })
                    .map((mentor, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-4">
                            {/* Name & Email */}
                            <div>
                              <div className="font-medium">{`${mentor.firstName} ${mentor.lastName}`}</div>
                              <div className="text-gray-500 text-sm">
                                {mentor.email}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="py-4 px-6 text-center">
                          {formattedDate(mentor.createdAt)}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span
                            className={`px-3 py-1 rounded text-sm font-semibold ${
                              statusColors[mentor.isVerified]
                            }`}
                          >
                            {mentor.isVerified ? "Completed" : "Pending"}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-center">
                          {(mentor.reviews?.averageRating ?? 0.0).toFixed(1)}
                        </td>
                        <td className="py-4 px-6 space-x-2 flex justify-center items-center gap-2">
                          <button
                            onClick={() => navigate(mentor.userId)}
                            className="text-[#1DB9AA] hover:underline flex items-center gap-1 bg-[#02B6B31F] px-5 py-2 rounded text-sm whitespace-nowrap"
                          >
                            <Eye className="text-[#1DB9AA] w-3" /> View Profile
                          </button>
                          <button
                            onClick={() => handleModalOpen(mentor.userId)}
                            className="text-white hover:opacity-90 flex items-center gap-1 bg-[#7E5BEF] px-5 py-2 rounded text-sm whitespace-nowrap"
                          >
                            <Send className="w-3" /> Send Email
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default MentorsList;
